import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import BlockchainProofTable from '../components/BlockchainProofTable';
import TxDetailModal from '../components/TxDetailModal';
import { Copy, QrCode, ExternalLink, Shield, CheckCircle } from 'lucide-react';
import { calculateTotal } from '../utils/csvExport';
import transactionsData from '../data/transactions.json';
import '../styles/blockchain.css';

const BlockchainProof = () => {
  const [transactions, setTransactions] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const publicWallet = '0xa3b1f9c4d2e6b7f8c9a0d4e5f6b7c8d9e0a1b2c3';
  const chainId = '1546';

  useEffect(() => {
    setTransactions(transactionsData);
  }, []);

  const handleTransactionClick = (transaction) => {
    setSelectedTransaction(transaction);
    setIsModalOpen(true);
  };

  const handleCopyWallet = () => {
    navigator.clipboard.writeText(publicWallet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const totalAmount = calculateTotal(transactions);
  const totalTransactions = transactions.length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Blockchain Proof</h1>
          <p className="text-xl text-gray-600 mb-8">
            Every donation recorded. Every expense verifiable.
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Wallet Address</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <code className="text-sm font-mono text-gray-900 flex-1 truncate">
                  {publicWallet.slice(0, 10)}...{publicWallet.slice(-8)}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyWallet}
                  className="shrink-0"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              {copied && (
                <p className="text-xs text-green-600 mt-1">Copied!</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Chain ID</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-gray-900">{chainId}</span>
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  Dot Protocol
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{totalTransactions}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Amount (USD)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                ${totalAmount.toLocaleString()}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Wallet Info Card */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-600" />
              Kibouor Foundation Public Wallet
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium text-gray-600 mb-2 block">Wallet Address</label>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <code className="flex-1 font-mono text-sm">{publicWallet}</code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyWallet}
                    className="shrink-0"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  This is the public Kibouor demo wallet. For live donations, we will connect the official wallets.
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-600 mb-2 block">QR Code</label>
                <div className="p-4 bg-white border-2 border-dashed border-gray-300 rounded-lg text-center">
                  <QrCode className="w-16 h-16 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500">QR Code for wallet address</p>
                </div>
              </div>
            </div>
            
            <div className="mt-6 pt-6 border-t">
              <Button
                onClick={() => window.open(`https://explorer.dotprotocol.org/address/${publicWallet}`, '_blank')}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                View on Dot Protocol Explorer
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Mini Explorer */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Live Explorer Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">1,247,892</div>
                <div className="text-sm text-green-700">Latest Block</div>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{totalTransactions}</div>
                <div className="text-sm text-blue-700">Kibouor Transactions</div>
              </div>
              <div className="text-center p-4 bg-amber-50 rounded-lg">
                <div className="text-2xl font-bold text-amber-600">2.3s</div>
                <div className="text-sm text-amber-700">Avg Block Time</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Transactions Table */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Transaction History</h2>
          <BlockchainProofTable 
            transactions={transactions} 
            onTransactionClick={handleTransactionClick}
          />
        </div>

        {/* Verification Instructions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-600" />
              How to Verify Transactions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none">
              <p className="text-gray-700 mb-4">
                <strong>How to verify:</strong> Click any transaction → open its detail modal → select "View on Dot Protocol Explorer" to see the on-chain record. 
                All transactions listed here are publicly verifiable on the Dot Protocol chain (Chain ID 1546) using the Kibouor Foundation public address: 
                <code className="ml-1 font-mono text-xs bg-gray-100 px-1 rounded">0xa3b1f9c4d2e6b7f8c9a0d4e5f6b7c8d9e0a1b2c3</code>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Back to Home */}
        <div className="mt-12 pt-8 border-t border-gray-200 text-center">
          <a href="/" className="text-amber-600 hover:underline">
            ← Back to Home
          </a>
        </div>
      </div>

      {/* Transaction Detail Modal */}
      <TxDetailModal
        transaction={selectedTransaction}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default BlockchainProof;
